import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Lightning, Fire, Target, ChartLineUp, Buildings, MapPin, Phone, User,
  ArrowRight, Sparkle, Brain, MagnifyingGlass, Crosshair, CalendarBlank,
  ArrowsClockwise, CaretRight, Briefcase, CurrencyGbp, TrendUp, WarningCircle,
  ChatCircle, UserPlus, CheckCircle, XCircle, Star, Gear
} from "@phosphor-icons/react";
import { toast } from "sonner";
import CallButton from "@/components/CallButton";
import Pagination from "@/components/Pagination";

const PRIORITY_CONFIG = {
  hot: { color: "#DC2626", bg: "bg-red-50", border: "border-red-200", icon: Fire, label: "HOT" },
  warm: { color: "#F59E0B", bg: "bg-amber-50", border: "border-amber-200", icon: Target, label: "WARM" },
  market_intelligence: { color: "#3B82F6", bg: "bg-blue-50", border: "border-blue-200", icon: ChartLineUp, label: "INTEL" },
};

const FEEDBACK_OPTIONS = [
  { value: "strong_lead", label: "Strong Lead", icon: Star },
  { value: "good_lead", label: "Good Lead", icon: CheckCircle },
  { value: "weak_lead", label: "Weak Lead", icon: WarningCircle },
  { value: "not_hiring", label: "Not Hiring", icon: XCircle },
  { value: "existing_client", label: "Existing Client", icon: Buildings },
  { value: "wrong_sector", label: "Wrong Sector", icon: Crosshair },
  { value: "duplicate", label: "Duplicate", icon: ArrowsClockwise },
];

const SECTORS = [
  "Healthcare & Social Care", "IT & Technology", "Accounting & Finance",
  "Education & Childcare", "Commercial Sales & Business Development",
  "Sales, Revenue & Customer Success",
];

export default function IntelligencePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManageSectors = user?.role === "admin" || user?.role === "team_leader";
  const [dashboard, setDashboard] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const [sectorFilters, setSectorFilters] = useState([]);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [activeSectors, setActiveSectors] = useState([]);
  const [showSectorConfig, setShowSectorConfig] = useState(false);
  const [savingSectors, setSavingSectors] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [promoting, setPromoting] = useState(false);
  const [page, setPage] = useState(1);
  const [roleFamily, setRoleFamily] = useState("all");
  const PAGE_SIZE = 25;

  const ROLE_FAMILIES = [
    "Sales Development", "Business Development", "Customer Success",
    "Account Management", "Account Executive", "Sales Leadership", "Revenue Leadership",
  ];

  // Load active sectors config
  useEffect(() => {
    if (canManageSectors) {
      api.get("/intelligence/config").then(({ data }) => {
        setActiveSectors(data.active_sectors || []);
      }).catch(() => {});
    }
  }, [canManageSectors]);

  const loadDashboard = useCallback(async () => {
    try {
      const { data } = await api.get("/intelligence/dashboard");
      setDashboard(data);
    } catch (err) { console.warn("Dashboard load error:", err?.message); }
  }, []);

  const loadOpportunities = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (priorityFilter && priorityFilter !== "all") params.set("priority", priorityFilter);
      if (sectorFilters.length > 0) params.set("sector", sectorFilters.join(","));
      if (roleFamily && roleFamily !== "all") params.set("role_family", roleFamily);
      params.set("sort", sortBy);
      params.set("page", String(page));
      params.set("limit", String(PAGE_SIZE));
      const { data } = await api.get(`/intelligence/opportunities?${params}`);
      setOpportunities(data.items || []);
      setTotal(data.total || 0);
    } catch (err) { console.warn("Opportunities load error:", err?.message); }
    setLoading(false);
  }, [priorityFilter, sectorFilters, page, sortBy, roleFamily]);

  useEffect(() => { loadDashboard(); loadOpportunities(); }, [loadDashboard, loadOpportunities]);
  // Filter changes reset paging — otherwise a narrower result set can leave you
  // stranded on a page that no longer exists.
  useEffect(() => { setPage(1); }, [priorityFilter, sectorFilters, sortBy, roleFamily]);
  useEffect(() => {
    api.get("/intelligence/team-members").then(r => setTeamMembers(r.data || [])).catch(() => {});
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/intelligence/generate", {});
      toast.success(`${data.message} — ${data.hot} HOT, ${data.warm} WARM`);
      loadDashboard();
      loadOpportunities();
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    setGenerating(false);
  };

  const handleScanSectors = async (sectors) => {
    setGenerating(true);
    try {
      const { data } = await api.post("/intelligence/generate", { sectors });
      toast.success(`${data.message} — ${data.hot} HOT, ${data.warm} WARM`);
      loadDashboard();
      loadOpportunities();
    } catch (err) { toast.error(err.response?.data?.detail || "Scan failed"); }
    setGenerating(false);
  };

  const saveActiveSectors = async () => {
    setSavingSectors(true);
    try {
      await api.put("/intelligence/config", { active_sectors: activeSectors });
      toast.success("Daily active sectors saved");
      setShowSectorConfig(false);
    } catch { toast.error("Failed to save"); }
    setSavingSectors(false);
  };

  const handleRefresh = async () => {
    try {
      const { data } = await api.post("/intelligence/refresh-market");
      toast.success(data.message);
    } catch (err) { toast.error("Refresh failed"); }
  };

  const openDetail = async (opp) => {
    try {
      const { data } = await api.get(`/intelligence/opportunities/${opp.id}`);
      setSelectedOpp(data);
    } catch { setSelectedOpp(opp); }
    setShowDetail(true);
  };

  const generateBrief = async () => {
    if (!selectedOpp) return;
    setBriefLoading(true);
    try {
      const { data } = await api.post(`/intelligence/opportunities/${selectedOpp.id}/generate-brief`);
      setSelectedOpp(prev => ({ ...prev, call_brief: data.brief }));
      toast.success("Call brief generated");
    } catch (err) { toast.error("Brief generation failed"); }
    setBriefLoading(false);
  };

  const handleAssign = async (recruiterId) => {
    if (!selectedOpp || !recruiterId) return;
    try {
      const { data } = await api.post(`/intelligence/opportunities/${selectedOpp.id}/assign`, { recruiter_id: recruiterId });
      toast.success(data.message);
      setSelectedOpp(prev => ({ ...prev, assigned_to: recruiterId, assigned_to_name: teamMembers.find(m => m.id === recruiterId)?.name || "", pipeline_status: "assigned" }));
      loadOpportunities();
    } catch (err) { toast.error("Assignment failed"); }
  };

  const handleFeedback = async (feedbackType) => {
    if (!selectedOpp) return;
    try {
      await api.post(`/intelligence/opportunities/${selectedOpp.id}/feedback`, { feedback: feedbackType });
      toast.success("Feedback recorded");
      setSelectedOpp(prev => ({ ...prev, last_feedback: feedbackType }));
      loadOpportunities();
    } catch { toast.error("Feedback failed"); }
  };

  const promoteSelected = async () => {
    if (selectedIds.size === 0) { toast.error("Select opportunities first"); return; }
    setPromoting(true);
    try {
      const { data } = await api.post("/intelligence/opportunities/promote-to-leads", { opportunity_ids: [...selectedIds] });
      toast.success(`${data.created} leads created, ${data.skipped} skipped`);
      setSelectedIds(new Set());
      loadOpportunities();
    } catch (e) { toast.error(e.response?.data?.detail || "Promote failed"); }
    finally { setPromoting(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const d = dashboard || {};
  const greeting = new Date().getHours() < 12 ? "Good Morning" : new Date().getHours() < 17 ? "Good Afternoon" : "Good Evening";

  return (
    <div data-testid="intelligence-page" className="space-y-5">
      {/* Header Card */}
      <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-6 text-white" data-testid="intelligence-header">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Brain size={24} weight="duotone" className="text-[#C5A059]" />
              <h1 className="text-lg font-bold">Talent Engine</h1>
            </div>
            <p className="text-white/60 text-sm">{greeting} — {user?.name}</p>
          </div>
          <div className="flex gap-2">
            {canManageSectors && (
              <Button onClick={() => setShowSectorConfig(true)} variant="outline" size="sm" className="text-xs bg-transparent border-white/20 text-white hover:bg-white/10 gap-1" data-testid="sector-config-btn">
                <Gear size={14} /> Sectors
              </Button>
            )}
            <Button onClick={handleRefresh} variant="outline" size="sm" className="text-xs bg-transparent border-white/20 text-white hover:bg-white/10 gap-1" data-testid="refresh-market-btn">
              <ArrowsClockwise size={14} /> Refresh Market
            </Button>
            <Button onClick={handleGenerate} disabled={generating} size="sm" className="text-xs bg-[#C5A059] text-[#0F172A] hover:bg-[#D4AF6F] font-bold gap-1" data-testid="generate-leads-btn">
              <Sparkle size={14} weight="fill" /> {generating ? "Generating..." : "Generate Today's Leads"}
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
          <StatCard icon={Fire} label="HOT" value={d.hot_count || 0} color="#EF4444" />
          <StatCard icon={Target} label="WARM" value={d.warm_count || 0} color="#F59E0B" />
          <StatCard icon={ChartLineUp} label="Market Intel" value={d.market_intelligence_count || 0} color="#3B82F6" />
          <StatCard icon={Sparkle} label="High Confidence" value={d.high_confidence || 0} color="#C5A059" />
        </div>
        {d.last_scan && (
          <p className="text-white/40 text-[10px] mt-3">Last scan: {new Date(d.last_scan).toLocaleString("en-GB")} · {d.new_signals || 0} signals detected</p>
        )}
        {canManageSectors && activeSectors.length > 0 && (
          <p className="text-white/30 text-[10px] mt-1">Daily active sectors: {activeSectors.join(", ")}</p>
        )}
      </div>

      {/* Active Sectors Config Dialog */}
      <Dialog open={showSectorConfig} onOpenChange={setShowSectorConfig}>
        <DialogContent className="sm:max-w-md" data-testid="sector-config-dialog">
          <DialogHeader><DialogTitle className="text-[#0F172A] flex items-center gap-2"><Gear size={18} className="text-[#C5A059]" /> Daily Intelligence Active Sectors</DialogTitle></DialogHeader>
          <p className="text-xs text-[#6B7280] -mt-2">Select which sectors are included in the automatic 08:00 daily feed.</p>
          <div className="space-y-2 mt-2">
            {SECTORS.map(s => {
              const active = activeSectors.includes(s);
              return (
                <button key={s} onClick={() => setActiveSectors(prev => active ? prev.filter(x => x !== s) : [...prev, s])}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all ${active ? "bg-[#0F172A] text-[#C5A059] border-[#0F172A]" : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#9CA3AF]"}`}
                  data-testid={`active-sector-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center text-xs font-bold ${active ? "bg-[#C5A059] text-[#0F172A]" : "bg-[#E5E7EB] text-[#9CA3AF]"}`}>
                    {active ? "✓" : ""}
                  </div>
                  <span className="text-sm font-medium">{s}</span>
                </button>
              );
            })}
          </div>
          <div className="flex justify-between items-center mt-3">
            <span className="text-[10px] text-[#9CA3AF]">{activeSectors.length} sector{activeSectors.length !== 1 ? "s" : ""} active</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowSectorConfig(false)}>Cancel</Button>
              <Button size="sm" onClick={saveActiveSectors} disabled={savingSectors || activeSectors.length === 0} className="bg-[#0F172A]" data-testid="save-sectors-btn">
                {savingSectors ? "Saving..." : "Save Configuration"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="flex gap-2">
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="priority-filter"><SelectValue placeholder="All Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="hot">HOT</SelectItem>
            <SelectItem value="warm">WARM</SelectItem>
            <SelectItem value="market_intelligence">Market Intel</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[150px] h-9 border-[#E5E7EB]" data-testid="sort-filter"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="score">Score (highest)</SelectItem>
            <SelectItem value="name_asc">A to Z</SelectItem>
            <SelectItem value="name_desc">Z to A</SelectItem>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
          </SelectContent>
        </Select>
        <Select value={roleFamily} onValueChange={setRoleFamily}>
          <SelectTrigger className="w-[180px] h-9 border-[#E5E7EB]" data-testid="role-family-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLE_FAMILIES.map(rf => <SelectItem key={rf} value={rf}>{rf}</SelectItem>)}
          </SelectContent>
        </Select>
        {isAdmin && (
          <div className="flex flex-wrap gap-1.5">
            {SECTORS.map(s => {
              const active = sectorFilters.includes(s);
              return (
                <button key={s} onClick={() => setSectorFilters(prev => active ? prev.filter(x => x !== s) : [...prev, s])}
                  className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-colors ${active ? "bg-[#0F172A] text-[#C5A059] border-[#0F172A]" : "bg-white text-[#6B7280] border-[#E5E7EB] hover:border-[#9CA3AF]"}`}
                  data-testid={`sector-toggle-${s.replace(/\s+/g, "-").toLowerCase()}`}>{s}</button>
              );
            })}
            {sectorFilters.length > 0 && (
              <button onClick={() => setSectorFilters([])} className="px-2 py-1.5 text-[10px] text-[#9CA3AF] hover:text-[#EF4444]">Clear</button>
            )}
          </div>
        )}
        <span className="text-sm text-[#6B7280] self-center ml-2">{total} opportunities</span>
        {selectedIds.size > 0 && (
          <Button size="sm" className="ml-auto bg-[#C5A059] text-white gap-1 text-xs" onClick={promoteSelected} disabled={promoting} data-testid="promote-to-leads-btn">
            {promoting ? "Promoting..." : `Promote ${selectedIds.size} to Leads`}
          </Button>
        )}
      </div>

      {/* Opportunity Cards */}
      <div className="space-y-3" data-testid="opportunity-list">
        {loading ? (
          [1,2,3].map(i => <div key={i} className="h-28 skeleton-shimmer rounded-lg" />)
        ) : opportunities.length === 0 ? (
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
            <Brain size={48} className="mx-auto text-[#D1D5DB] mb-3" />
            <p className="text-[#6B7280] font-medium">No opportunities yet</p>
            <p className="text-xs text-[#9CA3AF] mt-1">Click "Generate Today's Leads" to start</p>
          </div>
        ) : opportunities.map(opp => {
          const pri = PRIORITY_CONFIG[opp.priority] || PRIORITY_CONFIG.market_intelligence;
          const PriIcon = pri.icon;
          return (
            <div key={opp.id} className={`bg-white rounded-lg border ${pri.border} p-4 hover:shadow-md transition-all cursor-pointer flex gap-3`}
              data-testid={`opp-card-${opp.id}`}>
              <input type="checkbox" checked={selectedIds.has(opp.id)} onChange={(e) => { e.stopPropagation(); toggleSelect(opp.id); }} onClick={(e) => e.stopPropagation()} className="mt-1 flex-shrink-0 accent-[#C5A059]" data-testid={`opp-check-${opp.id}`} />
              <div className="flex-1 min-w-0" onClick={() => openDetail(opp)}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-lg ${pri.bg} flex items-center justify-center flex-shrink-0`}>
                  <PriIcon size={20} weight="duotone" style={{ color: pri.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-[#0F172A]">{opp.company_name}</h3>
                    <Badge className={`text-[9px] ${pri.bg} border ${pri.border}`} style={{ color: pri.color }}>{pri.label}</Badge>
                    <Badge variant="outline" className="text-[9px]">{opp.sector}</Badge>
                    {opp.assigned_to_name && <Badge className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200">Assigned: {opp.assigned_to_name}</Badge>}
                  </div>
                  <p className="text-xs text-[#6B7280] mt-1 line-clamp-2">{opp.why_flagged || `${opp.total_vacancy_count} relevant vacancies detected`}</p>
                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    <span className="text-[10px] text-[#6B7280] flex items-center gap-1"><Briefcase size={10} /> {opp.total_vacancy_count} vacancies</span>
                    <span className="text-[10px] text-[#6B7280] flex items-center gap-1"><MapPin size={10} /> {opp.locations?.length || 0} locations</span>
                    {opp.company_phone && (
                      <span className="text-[10px] text-[#1E3A8A] flex items-center gap-1 font-medium" data-testid={`opp-phone-${opp.id}`}>
                        <Phone size={10} weight="fill" /> {opp.company_phone}
                      </span>
                    )}
                    {opp.placement_potential?.estimated_total_revenue > 0 && (
                      <span className="text-[10px] text-emerald-700 flex items-center gap-1 font-medium"><CurrencyGbp size={10} /> Est. £{(opp.placement_potential.estimated_total_revenue / 1000).toFixed(0)}k</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <div className="text-center">
                    <p className="text-lg font-black" style={{ color: pri.color }}>{opp.opportunity_score}</p>
                    <p className="text-[9px] text-[#9CA3AF] uppercase">Score</p>
                  </div>
                </div>
              </div>
              </div>
            </div>
          );
        })}
        {!loading && total > PAGE_SIZE && (
          <Pagination page={page} total={total} limit={PAGE_SIZE} onPageChange={(p) => { setPage(p); setLoading(true); }} />
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="opp-detail-dialog">
          {selectedOpp && (() => {
            const pri = PRIORITY_CONFIG[selectedOpp.priority] || PRIORITY_CONFIG.market_intelligence;
            const PriIcon = pri.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="text-[#0F172A] text-lg flex items-center gap-2">
                    <PriIcon size={20} weight="duotone" style={{ color: pri.color }} />
                    {selectedOpp.company_name}
                    <Badge className={`text-[10px] ${pri.bg} border ${pri.border} ml-1`} style={{ color: pri.color }}>{pri.label}</Badge>
                  </DialogTitle>
                </DialogHeader>

                {/* Scores */}
                <div className="grid grid-cols-4 gap-2">
                  <ScoreCard label="Opportunity" value={selectedOpp.opportunity_score} color={pri.color} />
                  <ScoreCard label="ICP Fit" value={selectedOpp.icp_score} color="#1E3A8A" />
                  <ScoreCard label="Hiring Stress" value={selectedOpp.hiring_stress_score} color="#EF4444" />
                  <ScoreCard label="Placement" value={selectedOpp.placement_potential?.estimated_placements || 0} color="#10B981" suffix="x" />
                </div>

                {/* Why flagged */}
                {selectedOpp.why_flagged && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-[10px] font-bold text-amber-800 uppercase tracking-wide mb-1">Why We Are Flagging This Company</p>
                    <p className="text-xs text-amber-900">{selectedOpp.why_flagged}</p>
                  </div>
                )}
                {selectedOpp.why_now && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-[10px] font-bold text-red-800 uppercase tracking-wide mb-1">Why Now?</p>
                    <p className="text-xs text-red-900">{selectedOpp.why_now}</p>
                  </div>
                )}

                {/* Vacancies with Source URLs */}
                <div>
                  <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide mb-2">Relevant Vacancies ({selectedOpp.total_vacancy_count})</p>
                  <div className="space-y-1.5">
                    {(selectedOpp.vacancies || []).slice(0, 12).map((v, i) => (
                      <div key={i} className="bg-[#F9FAFB] rounded-lg px-3 py-2 border border-[#E5E7EB]">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-[#0F172A]">{v.title}</span>
                          <div className="flex items-center gap-1.5">
                            {v.verification_status === "verified" && <span className="w-1.5 h-1.5 rounded-full bg-green-500" title="Verified" />}
                            {v.location && <span className="text-[9px] text-[#9CA3AF]">{v.location}</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-[9px] text-[#9CA3AF]">{v.source}</span>
                          {v.first_seen && <span className="text-[9px] text-[#9CA3AF]">First seen: {new Date(v.first_seen).toLocaleDateString("en-GB")}</span>}
                          {v.last_verified && <span className="text-[9px] text-[#9CA3AF]">Verified: {new Date(v.last_verified).toLocaleDateString("en-GB")}</span>}
                          {v.source_url && (
                            <a href={v.source_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                              className="text-[9px] text-[#1E3A8A] hover:underline font-medium">View Source</a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact + Phone */}
                {(selectedOpp.primary_contact?.name || selectedOpp.company_phone) && (
                  <div>
                    <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide mb-2">Contact</p>
                    {selectedOpp.primary_contact?.name && (
                      <div className="flex items-center gap-2 mb-2">
                        <User size={14} className="text-[#6B7280]" />
                        <span className="text-xs font-medium text-[#0F172A]">{selectedOpp.primary_contact.name}</span>
                        <span className="text-[10px] text-[#6B7280]">{selectedOpp.primary_contact.title}</span>
                        <Badge variant="outline" className="text-[9px]">{selectedOpp.primary_contact.confidence} conf.</Badge>
                      </div>
                    )}
                    {selectedOpp.company_phone && <CallButton phone={selectedOpp.company_phone} label="Company" />}
                  </div>
                )}

                {/* Signals */}
                {selectedOpp.signals?.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide mb-2">Hiring Signals ({selectedOpp.signals.length})</p>
                    <div className="space-y-1">
                      {selectedOpp.signals.slice(0, 8).map((s, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <TrendUp size={12} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                          <span className="text-[#374151]"><strong className="text-[#0F172A]">{(s.type || "").replace(/_/g, " ")}:</strong> {s.summary}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Placement Potential */}
                {selectedOpp.placement_potential && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-[10px] font-bold text-emerald-800 uppercase tracking-wide mb-1">Estimated Recruitment Value</p>
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-bold text-emerald-700">£{(selectedOpp.placement_potential.estimated_total_revenue || 0).toLocaleString()}</span>
                      <span className="text-[10px] text-emerald-600">{selectedOpp.placement_potential.estimated_placements} placements × £{(selectedOpp.placement_potential.estimated_fee_per_placement || 0).toLocaleString()}/fee</span>
                      <Badge variant="outline" className="text-[9px] border-emerald-200 text-emerald-700">{selectedOpp.placement_potential.confidence} confidence</Badge>
                    </div>
                  </div>
                )}

                {/* Call Brief */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide">AI Call Brief</p>
                    <Button onClick={generateBrief} disabled={briefLoading} size="sm" variant="outline" className="text-xs h-7 gap-1" data-testid="generate-brief-btn">
                      <Brain size={12} /> {briefLoading ? "Generating..." : "Generate Brief"}
                    </Button>
                  </div>
                  {selectedOpp.call_brief ? (
                    <div className="bg-[#F9FAFB] rounded-lg p-3 text-xs text-[#374151] whitespace-pre-wrap border border-[#E5E7EB]">{selectedOpp.call_brief}</div>
                  ) : (
                    <p className="text-xs text-[#9CA3AF]">Click "Generate Brief" to create an AI-powered call preparation brief</p>
                  )}
                </div>

                <Separator />

                {/* Actions */}
                <div className="space-y-3">
                  {/* Assign */}
                  <div>
                    <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide mb-1">Assign to Recruiter</p>
                    <Select value={selectedOpp.assigned_to || "unassigned"} onValueChange={v => handleAssign(v === "unassigned" ? "" : v)}>
                      <SelectTrigger className="h-9 border-[#E5E7EB]" data-testid="assign-opp-select"><SelectValue placeholder="Assign..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {teamMembers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Feedback */}
                  <div>
                    <p className="text-[10px] font-bold text-[#6B7280] uppercase tracking-wide mb-1">Feedback</p>
                    <div className="flex flex-wrap gap-1.5">
                      {FEEDBACK_OPTIONS.map(fb => (
                        <Button key={fb.value} size="sm" variant={selectedOpp.last_feedback === fb.value ? "default" : "outline"}
                          className={`text-[10px] h-7 gap-1 ${selectedOpp.last_feedback === fb.value ? "bg-[#0F172A] text-white" : "border-[#E5E7EB]"}`}
                          onClick={() => handleFeedback(fb.value)} data-testid={`feedback-${fb.value}`}>
                          <fb.icon size={10} /> {fb.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white/10 rounded-lg p-3 text-center">
      <Icon size={18} weight="duotone" style={{ color }} className="mx-auto mb-1" />
      <p className="text-xl font-black text-white">{value}</p>
      <p className="text-[9px] text-white/50 uppercase tracking-wide">{label}</p>
    </div>
  );
}

function ScoreCard({ label, value, color, suffix = "" }) {
  return (
    <div className="bg-[#F9FAFB] rounded-lg p-2.5 text-center border border-[#E5E7EB]">
      <p className="text-lg font-black" style={{ color }}>{value}{suffix}</p>
      <p className="text-[9px] text-[#6B7280] uppercase tracking-wide">{label}</p>
    </div>
  );
}
